import { icons } from '../assets/icons';
import { GitHubWikiService, ParsedDocResult } from '../services/github-wiki';
import { WIKI_PAGES, PROJECT_CONFIG } from '../config/project-info';
import { GiscusWidget } from './GiscusWidget';

export class DocsViewerComponent {
  private currentSlug: string = 'Home';
  private docData: ParsedDocResult | null = null;
  private isLoading: boolean = false;
  private errorMessage: string | null = null;
  private searchQuery: string = '';
  private docGiscus: GiscusWidget | null = null;

  constructor() {
    //
  }

  public async loadPage(slug: string = 'Home') {
    if (this.docGiscus) {
      this.docGiscus.destroy();
      this.docGiscus = null;
    }

    this.currentSlug = slug || 'Home';
    this.isLoading = true;
    this.errorMessage = null;
    this.closeMobileMenus();
    this.updateContent();

    try {
      const data = await GitHubWikiService.getPage(this.currentSlug);
      this.docData = data;
      this.isLoading = false;
      this.updateContent();
      this.bindDocEvents();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error(err);
      this.isLoading = false;
      this.errorMessage = err.message || 'Failed to load documentation page.';
      this.updateContent();
    }
  }

  public render(): string {
    const meta = WIKI_PAGES.find(p => p.slug.toLowerCase() === this.currentSlug.toLowerCase()) || {
      slug: this.currentSlug,
      title: this.currentSlug.replace(/-/g, ' '),
      category: 'Getting Started',
      description: ''
    };

    return `
      <div id="docs-viewer-container" class="min-h-screen pb-20 font-mono">
        
        <!-- Mobile Sticky Sub-Header / Action Bar (< lg screens) -->
        <div class="lg:hidden sticky top-16 z-30 w-full bg-terminal-bgDark/90 backdrop-blur-md border-b border-terminal-border px-4 py-2.5 transition-colors">
          <div class="max-w-7xl mx-auto flex items-center justify-between gap-2">
            
            <!-- Mobile Topics Drawer Trigger -->
            <button 
              id="docs-mobile-topics-btn"
              class="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-matrix-950/90 text-matrix-300 border border-matrix-700/60 hover:bg-matrix-900 transition-colors cursor-pointer"
            >
              ${icons.bookOpen('w-4 h-4 text-matrix-400')}
              <span class="truncate max-w-[140px] sm:max-w-[200px]">${meta.title}</span>
              ${icons.chevronDown('w-3.5 h-3.5')}
            </button>

            <!-- Mobile In-Page TOC Trigger -->
            <button 
              id="docs-mobile-toc-btn"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-terminal-fg bg-terminal-card hover:bg-terminal-cardLight border border-terminal-border transition-colors cursor-pointer"
            >
              ${icons.layers('w-3.5 h-3.5 text-cyber-400')}
              <span>On This Page</span>
              ${icons.chevronDown('w-3 h-3')}
            </button>

          </div>

          <!-- Mobile Collapsible TOC Dropdown -->
          <div id="docs-mobile-toc-dropdown" class="hidden mt-2 pt-2 border-t border-terminal-border max-h-60 overflow-y-auto">
            <div class="text-[11px] font-semibold text-terminal-muted uppercase tracking-wider mb-2">Sections on this page</div>
            <div id="docs-mobile-toc-list" class="space-y-1">
              ${this.renderToc(true)}
            </div>
          </div>
        </div>

        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            <!-- Desktop Left Sidebar Navigation (3 cols) -->
            <aside class="hidden lg:block lg:col-span-3 lg:sticky lg:top-24 z-10 space-y-5">
              
              <!-- Search Box -->
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-matrix-600">
                  ${icons.search('w-4 h-4')}
                </div>
                <input 
                  type="text" 
                  id="docs-search-input"
                  placeholder="Search documentation..."
                  value="${this.searchQuery}"
                  class="w-full pl-9 pr-4 py-2 text-xs rounded-xl bg-terminal-card border border-terminal-border text-terminal-fg placeholder-terminal-muted focus:outline-none focus:border-matrix-500 focus:shadow-glow-sm transition-all"
                />
              </div>

              <!-- Topics Navigation Categories -->
              <div id="docs-sidebar-links" class="space-y-5 max-h-[calc(100vh-180px)] overflow-y-auto pr-2">
                ${this.renderSidebarCategories()}
              </div>

            </aside>

            <!-- Main Content Area (6-7 cols on desktop, full width on mobile) -->
            <main class="w-full lg:col-span-9 xl:col-span-7 bg-terminal-card p-5 sm:p-8 lg:p-10 rounded-2xl border border-terminal-border shadow-glow-lg min-h-[600px] overflow-hidden text-left">
              <div id="docs-main-content">
                ${this.renderContentBody()}
              </div>
            </main>

            <!-- Desktop Right Sidebar: In-Page Table of Contents (2-3 cols) -->
            <aside class="hidden xl:block xl:col-span-2 xl:sticky xl:top-24 z-10 space-y-4">
              <div id="docs-toc-container" class="p-4 rounded-xl bg-terminal-card border border-terminal-border text-xs space-y-3">
                <div class="font-bold text-matrix-400 uppercase tracking-wider flex items-center gap-1.5">
                  ${icons.layers('w-3.5 h-3.5 text-cyber-400')}
                  <span>On This Page</span>
                </div>
                <div id="docs-toc-list">
                  ${this.renderToc()}
                </div>
              </div>

              <!-- Quick Links Box -->
              <div class="p-4 rounded-xl bg-terminal-card border border-terminal-border text-xs space-y-2.5">
                <div class="font-bold text-matrix-400">Community & Help</div>
                <a href="#/community" class="text-terminal-muted hover:text-cyber-300 flex items-center gap-1.5 transition-colors">
                  ${icons.messageSquare('w-3.5 h-3.5 text-cyber-400')}
                  <span>Discussions Board</span>
                </a>
                <a href="${PROJECT_CONFIG.githubNewIssueUrl}" target="_blank" class="text-terminal-muted hover:text-rose-400 flex items-center gap-1.5 transition-colors">
                  ${icons.bug('w-3.5 h-3.5 text-rose-400')}
                  <span>Report an Issue</span>
                </a>
              </div>
            </aside>

          </div>
        </div>

        <!-- Mobile Slide-over Drawer for Topics -->
        <div 
          id="docs-mobile-drawer-overlay"
          class="fixed inset-0 z-50 hidden bg-black/80 backdrop-blur-sm lg:hidden transition-opacity"
        >
          <div 
            id="docs-mobile-drawer-panel"
            class="fixed inset-y-0 left-0 w-full max-w-xs sm:max-w-sm bg-terminal-bgDark p-5 shadow-2xl z-50 flex flex-col space-y-4 border-r border-terminal-border"
          >
            <!-- Drawer Header -->
            <div class="flex items-center justify-between pb-3 border-b border-terminal-border">
              <div class="flex items-center gap-2 font-bold text-matrix-400">
                ${icons.bookOpen('w-5 h-5 text-matrix-500')}
                <span>Documentation Topics</span>
              </div>
              <button 
                id="docs-mobile-drawer-close"
                class="p-1.5 rounded-lg text-terminal-muted hover:text-terminal-fg hover:bg-terminal-card cursor-pointer"
                aria-label="Close menu"
              >
                ${icons.close('w-5 h-5')}
              </button>
            </div>

            <!-- Mobile Search Box -->
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-matrix-600">
                ${icons.search('w-4 h-4')}
              </div>
              <input 
                type="text" 
                id="docs-mobile-search-input"
                placeholder="Search topics..."
                value="${this.searchQuery}"
                class="w-full pl-9 pr-4 py-2 text-xs rounded-xl bg-terminal-card border border-terminal-border text-terminal-fg placeholder-terminal-muted focus:outline-none focus:border-matrix-500"
              />
            </div>

            <!-- Scrollable Topics List -->
            <div id="docs-mobile-sidebar-links" class="flex-1 overflow-y-auto space-y-5 pr-1 text-left">
              ${this.renderSidebarCategories(true)}
            </div>

          </div>
        </div>

      </div>
    `;
  }

  private renderSidebarCategories(isMobile = false): string {
    const categories = GitHubWikiService.getCategories();
    const query = this.searchQuery.toLowerCase().trim();

    return categories.map(cat => {
      const filteredPages = query 
        ? cat.pages.filter(p => p.title.toLowerCase().includes(query) || p.description.toLowerCase().includes(query) || p.slug.toLowerCase().includes(query))
        : cat.pages;

      if (filteredPages.length === 0) return '';

      return `
        <div class="space-y-1">
          <h4 class="text-[11px] font-bold uppercase tracking-wider text-matrix-500/80 px-2">
            ${cat.category}
          </h4>
          <div class="space-y-0.5">
            ${filteredPages.map(page => {
              const isActive = this.currentSlug.toLowerCase() === page.slug.toLowerCase();
              return `
                <a 
                  href="#/docs/${page.slug}"
                  class="${isMobile ? 'mobile-doc-link' : 'docs-nav-item'} flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isActive 
                      ? 'bg-matrix-950 text-matrix-300 font-bold border border-matrix-700 shadow-glow-sm' 
                      : 'text-terminal-muted hover:bg-terminal-card hover:text-terminal-fg'
                  }"
                >
                  <span class="truncate">${page.title}</span>
                  ${isActive ? `<span class="w-1.5 h-1.5 rounded-full bg-matrix-400"></span>` : ''}
                </a>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }).join('');
  }

  private renderToc(isMobile = false): string {
    if (!this.docData || !this.docData.toc || this.docData.toc.length === 0) {
      return `<p class="text-terminal-muted italic text-[11px] py-1">No section headers on this page</p>`;
    }

    return `
      <nav class="space-y-1">
        ${this.docData.toc.map(item => `
          <a 
            href="#${item.id}"
            class="${isMobile ? 'mobile-toc-anchor' : ''} block text-terminal-muted hover:text-matrix-300 transition-colors truncate py-0.5 text-xs ${
              item.level === 3 ? 'pl-2 text-[11px] text-terminal-muted/80' : ''
            }"
          >
            ${item.text}
          </a>
        `).join('')}
      </nav>
    `;
  }

  private renderContentBody(): string {
    if (this.isLoading) {
      return `
        <div class="space-y-6 animate-pulse py-8 font-mono">
          <div class="h-8 bg-terminal-bgDark rounded w-2/3"></div>
          <div class="h-4 bg-terminal-bgDark rounded w-1/3"></div>
          <div class="space-y-3 pt-4">
            <div class="h-4 bg-terminal-bgDark rounded"></div>
            <div class="h-4 bg-terminal-bgDark rounded w-5/6"></div>
            <div class="h-4 bg-terminal-bgDark rounded w-4/6"></div>
          </div>
          <div class="h-32 bg-terminal-bgDark rounded-xl"></div>
        </div>
      `;
    }

    if (this.errorMessage) {
      return `
        <div class="py-12 text-center space-y-4 font-mono">
          <div class="w-12 h-12 rounded-full bg-rose-950 text-rose-500 flex items-center justify-center mx-auto">
            ${icons.alertCircle('w-6 h-6')}
          </div>
          <h3 class="text-base font-bold text-white">Unable to load documentation</h3>
          <p class="text-xs text-terminal-muted max-w-md mx-auto">${this.errorMessage}</p>
          <button 
            id="docs-retry-btn"
            class="px-4 py-2 rounded-xl text-xs font-bold bg-matrix-500 text-terminal-bgDark hover:bg-matrix-400 transition-colors inline-flex items-center gap-1.5 cursor-pointer"
          >
            ${icons.refreshCw('w-4 h-4')}
            <span>Retry</span>
          </button>
        </div>
      `;
    }

    if (!this.docData) {
      return `<p class="text-terminal-muted">Select a topic from the sidebar.</p>`;
    }

    const currentIdx = WIKI_PAGES.findIndex(p => p.slug.toLowerCase() === this.currentSlug.toLowerCase());
    const prevPage = currentIdx > 0 ? WIKI_PAGES[currentIdx - 1] : null;
    const nextPage = currentIdx < WIKI_PAGES.length - 1 ? WIKI_PAGES[currentIdx + 1] : null;

    // Create Giscus widget for this document page
    this.docGiscus = new GiscusWidget({
      containerId: 'doc-giscus-container',
      mapping: 'specific',
      term: `Docs: ${this.docData.title}`,
      category: 'General',
      inputPosition: 'top'
    });

    return `
      <article class="space-y-6 max-w-full font-mono">
        
        <!-- Header Metadata & Live GitHub Wiki Badge -->
        <div class="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-terminal-border">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-matrix-950 text-matrix-400 border border-matrix-700 shadow-glow-sm">
              <span class="w-1.5 h-1.5 rounded-full bg-matrix-500 animate-pulse"></span>
              <span>Live GitHub Wiki</span>
            </span>
          </div>

          <div class="flex items-center gap-2 flex-wrap text-xs">
            <a 
              href="${this.docData.editUrl}"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-matrix-400 bg-terminal-bgDark hover:bg-terminal-cardLight hover:text-matrix-300 border border-terminal-border hover:border-matrix-500 transition-all"
              title="Contribute edits directly on GitHub Wiki"
            >
              ${icons.edit('w-3.5 h-3.5')}
              <span>Edit on Wiki</span>
            </a>
            <a 
              href="${this.docData.sourceUrl}"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-terminal-muted hover:text-terminal-fg bg-terminal-bgDark hover:bg-terminal-card border border-terminal-border transition-colors"
            >
              ${icons.github('w-3.5 h-3.5')}
              <span>Source</span>
            </a>
          </div>
        </div>

        <!-- Rendered Markdown Body -->
        <div class="prose-custom max-w-none overflow-hidden">
          ${this.docData.html}
        </div>

        <!-- Pagination (Prev / Next) -->
        <div class="pt-8 mt-8 border-t border-terminal-border grid grid-cols-1 sm:grid-cols-2 gap-4">
          ${prevPage ? `
            <a 
              href="#/docs/${prevPage.slug}"
              class="p-4 rounded-xl border border-terminal-border hover:border-matrix-500/60 bg-terminal-bgDark/60 hover:bg-terminal-bgDark group transition-all"
            >
              <div class="text-[11px] text-terminal-muted flex items-center gap-1">
                <span>← Previous</span>
              </div>
              <div class="text-xs font-bold text-matrix-400 group-hover:text-matrix-300 mt-1">
                ${prevPage.title}
              </div>
            </a>
          ` : `<div></div>`}

          ${nextPage ? `
            <a 
              href="#/docs/${nextPage.slug}"
              class="p-4 rounded-xl border border-terminal-border hover:border-matrix-500/60 bg-terminal-bgDark/60 hover:bg-terminal-bgDark group transition-all text-left sm:text-right sm:col-start-2"
            >
              <div class="text-[11px] text-terminal-muted flex items-center sm:justify-end gap-1">
                <span>Next →</span>
              </div>
              <div class="text-xs font-bold text-matrix-400 group-hover:text-matrix-300 mt-1">
                ${nextPage.title}
              </div>
            </a>
          ` : ''}
        </div>

        <!-- Embedded Page Discussions Section -->
        <div class="pt-8 mt-8 border-t border-terminal-border space-y-4">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="text-sm font-bold text-matrix-400 flex items-center gap-2">
                ${icons.messageSquare('w-4 h-4 text-cyber-400')}
                <span>Questions & Feedback</span>
              </h3>
              <p class="text-xs text-terminal-muted mt-0.5">
                Discuss this documentation page or ask questions via GitHub Discussions.
              </p>
            </div>

            <a 
              href="${PROJECT_CONFIG.githubDiscussionsUrl}/categories/q-a" 
              target="_blank" 
              rel="noopener noreferrer"
              class="text-xs font-semibold text-cyber-400 hover:underline flex items-center gap-1"
            >
              <span>Ask on GitHub</span>
              ${icons.externalLink('w-3 h-3')}
            </a>
          </div>

          <div class="p-4 sm:p-6 rounded-2xl bg-terminal-bgDark border border-terminal-border shadow-inner">
            ${this.docGiscus.render()}
          </div>
        </div>

      </article>
    `;
  }

  private updateContent() {
    const mainEl = document.getElementById('docs-main-content');
    if (mainEl) {
      mainEl.innerHTML = this.renderContentBody();
    }
    const tocEl = document.getElementById('docs-toc-list');
    if (tocEl) {
      tocEl.innerHTML = this.renderToc();
    }
    const mobileTocEl = document.getElementById('docs-mobile-toc-list');
    if (mobileTocEl) {
      mobileTocEl.innerHTML = this.renderToc(true);
    }
    const sidebarEl = document.getElementById('docs-sidebar-links');
    if (sidebarEl) {
      sidebarEl.innerHTML = this.renderSidebarCategories();
    }
    const mobileSidebarEl = document.getElementById('docs-mobile-sidebar-links');
    if (mobileSidebarEl) {
      mobileSidebarEl.innerHTML = this.renderSidebarCategories(true);
    }

    const meta = WIKI_PAGES.find(p => p.slug.toLowerCase() === this.currentSlug.toLowerCase());
    const mobileTopicTitle = document.querySelector('#docs-mobile-topics-btn span');
    if (mobileTopicTitle && meta) {
      mobileTopicTitle.textContent = meta.title;
    }
  }

  private closeMobileMenus() {
    const drawerOverlay = document.getElementById('docs-mobile-drawer-overlay');
    if (drawerOverlay) drawerOverlay.classList.add('hidden');
    const tocDropdown = document.getElementById('docs-mobile-toc-dropdown');
    if (tocDropdown) tocDropdown.classList.add('hidden');
  }

  public bindEvents() {
    const searchInput = document.getElementById('docs-search-input') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.searchQuery = (e.target as HTMLInputElement).value;
        const sidebarEl = document.getElementById('docs-sidebar-links');
        if (sidebarEl) sidebarEl.innerHTML = this.renderSidebarCategories();
        const mobileSidebarEl = document.getElementById('docs-mobile-sidebar-links');
        if (mobileSidebarEl) mobileSidebarEl.innerHTML = this.renderSidebarCategories(true);
      });
    }

    const mobileSearchInput = document.getElementById('docs-mobile-search-input') as HTMLInputElement;
    if (mobileSearchInput) {
      mobileSearchInput.addEventListener('input', (e) => {
        this.searchQuery = (e.target as HTMLInputElement).value;
        const sidebarEl = document.getElementById('docs-sidebar-links');
        if (sidebarEl) sidebarEl.innerHTML = this.renderSidebarCategories();
        const mobileSidebarEl = document.getElementById('docs-mobile-sidebar-links');
        if (mobileSidebarEl) mobileSidebarEl.innerHTML = this.renderSidebarCategories(true);
      });
    }

    const mobileTopicsBtn = document.getElementById('docs-mobile-topics-btn');
    const mobileDrawerOverlay = document.getElementById('docs-mobile-drawer-overlay');
    const mobileDrawerClose = document.getElementById('docs-mobile-drawer-close');

    if (mobileTopicsBtn && mobileDrawerOverlay) {
      mobileTopicsBtn.addEventListener('click', () => {
        mobileDrawerOverlay.classList.remove('hidden');
      });
    }

    if (mobileDrawerClose && mobileDrawerOverlay) {
      mobileDrawerClose.addEventListener('click', () => {
        mobileDrawerOverlay.classList.add('hidden');
      });
    }

    if (mobileDrawerOverlay) {
      mobileDrawerOverlay.addEventListener('click', (e) => {
        if (e.target === mobileDrawerOverlay) {
          mobileDrawerOverlay.classList.add('hidden');
        }
      });
    }

    const mobileTocBtn = document.getElementById('docs-mobile-toc-btn');
    const mobileTocDropdown = document.getElementById('docs-mobile-toc-dropdown');

    if (mobileTocBtn && mobileTocDropdown) {
      mobileTocBtn.addEventListener('click', () => {
        mobileTocDropdown.classList.toggle('hidden');
      });
    }

    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.mobile-doc-link') || target.closest('.mobile-toc-anchor')) {
        this.closeMobileMenus();
      }
    });

    this.bindDocEvents();
  }

  private bindDocEvents() {
    const retryBtn = document.getElementById('docs-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        this.loadPage(this.currentSlug);
      });
    }

    if (this.docGiscus) {
      this.docGiscus.mount();
    }
  }

  public destroy() {
    if (this.docGiscus) {
      this.docGiscus.destroy();
      this.docGiscus = null;
    }
  }
}
